import React, { useState, useRef, useCallback, useEffect } from 'react';
import { API_URL } from '../utils/config';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  Camera,
  X,
  Loader2,
  ScanLine,
  CheckCircle,
  AlertCircle,
  RotateCcw,
  Smartphone
} from 'lucide-react';


/**
 * DocumentScanner - Componente per scansione documenti con fotocamera
 * 
 * Usa la fotocamera POSTERIORE del dispositivo per inquadrare un documento,
 * estrae i dati via OCR e li passa al form. Nessuna foto viene salvata.
 * 
 * Props:
 *   - token: Token del guest check-in (richiesto per chiamare l'endpoint)
 *   - onDataExtracted: Callback con i dati estratti
 *   - onClose: Callback per chiudere lo scanner
 */
const DocumentScanner = ({ token, onDataExtracted, onClose }) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [cameraPermission, setCameraPermission] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Start camera with rear-facing preference
  const startCamera = useCallback(async () => {
    setError(null);
    
    try {
      // Request rear camera (environment facing)
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Fotocamera posteriore
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsStreaming(true);
        setCameraPermission('granted');
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraPermission('denied');
      
      if (err.name === 'NotAllowedError') {
        setError('Permesso fotocamera negato. Abilita l\'accesso alla fotocamera nelle impostazioni del browser.');
      } else if (err.name === 'NotFoundError') {
        setError('Nessuna fotocamera trovata. Assicurati che il dispositivo abbia una fotocamera.');
      } else {
        setError(`Errore fotocamera: ${err.message}`);
      }
    }
  }, []);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  // Capture frame and send to OCR
  const captureAndScan = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setIsScanning(true);
    setError(null);
    setResult(null);
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas size to video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw current frame to canvas
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      // Convert to base64 JPEG (smaller than PNG)
      const imageBase64 = canvas.toDataURL('image/jpeg', 0.9).split(',')[1];
      
      // Send to OCR endpoint with token
      const response = await fetch(`${API_URL}/api/guest-checkin/${token}/scan-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ image_base64: imageBase64 })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResult(data);
      } else {
        setError(data.error || 'Impossibile leggere il documento. Riprova.');
      }
    } catch (err) {
      console.error('Scan error:', err);
      setError('Errore durante la scansione. Riprova.');
    } finally {
      setIsScanning(false);
    }
  }, [token]);

  // Use extracted data
  const useData = useCallback(() => {
    if (result && onDataExtracted) {
      onDataExtracted(result);
      stopCamera();
      onClose?.();
    }
  }, [result, onDataExtracted, stopCamera, onClose]);

  // Reset and try again
  const resetScan = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Auto-start camera when component mounts
  useEffect(() => {
    startCamera();
  }, [startCamera]);

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="document-scanner">
      {/* Header */}
      <div className="bg-black/50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <ScanLine className="h-5 w-5" />
          <span className="font-medium">Scansione Documento</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { stopCamera(); onClose?.(); }}
          className="text-white hover:bg-white/20"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Camera View */}
      <div className="flex-1 relative overflow-hidden">
        {/* Video element */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{ transform: 'scaleX(1)' }} // No mirror for rear camera
        />
        
        {/* Canvas for capturing (hidden) */}
        <canvas ref={canvasRef} className="hidden" />
        
        {/* Overlay with document frame guide */}
        {isStreaming && !result && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative w-[90%] max-w-md aspect-[1.6/1]">
              {/* Corner markers */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />
              
              {/* Center text */}
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-white text-sm bg-black/50 px-3 py-1 rounded-full">
                  Inquadra il documento
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Loading overlay */}
        {isScanning && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <div className="text-center text-white">
              <Loader2 className="h-12 w-12 animate-spin mx-auto mb-3" />
              <p className="font-medium">Lettura in corso...</p>
              <p className="text-sm text-white/70">Estrazione dati in italiano</p>
            </div>
          </div>
        )}
        
        {/* Error state */}
        {error && !result && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-4">
            <Card className="max-w-sm w-full p-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-800 mb-2">Errore Scansione</h3>
              <p className="text-sm text-slate-600 mb-4">{error}</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => { stopCamera(); onClose?.(); }}>
                  Annulla
                </Button>
                <Button onClick={() => { setError(null); if (!isStreaming) startCamera(); }}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Riprova
                </Button>
              </div>
            </Card>
          </div>
        )}
        
        {/* Success result */}
        {result && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
            <Card className="max-w-md w-full p-6 my-4">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="h-6 w-6 text-green-500" />
                <h3 className="font-semibold text-slate-800">Dati Estratti</h3>
                <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                  {Math.round((result.confidence || 0) * 100)}% affidabilità
                </span>
              </div>
              
              <div className="space-y-2 text-sm mb-4">
                {result.tipo_documento && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Tipo:</span>
                    <span className="font-medium">{result.tipo_documento.replace('_', ' ').toUpperCase()}</span>
                  </div>
                )}
                {result.cognome && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Cognome:</span>
                    <span className="font-medium">{result.cognome}</span>
                  </div>
                )}
                {result.nome && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Nome:</span>
                    <span className="font-medium">{result.nome}</span>
                  </div>
                )}
                {result.data_nascita && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Data nascita:</span>
                    <span className="font-medium">{result.data_nascita}</span>
                  </div>
                )}
                {result.luogo_nascita && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Luogo nascita:</span>
                    <span className="font-medium">{result.luogo_nascita}</span>
                  </div>
                )}
                {result.sesso && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Sesso:</span>
                    <span className="font-medium">{result.sesso}</span>
                  </div>
                )}
                {result.nazionalita && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Nazionalità:</span>
                    <span className="font-medium">{result.nazionalita}</span>
                  </div>
                )}
                {result.numero_documento && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">N. Documento:</span>
                    <span className="font-medium font-mono">{result.numero_documento}</span>
                  </div>
                )}
                {result.data_scadenza && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Scadenza:</span>
                    <span className="font-medium">{result.data_scadenza}</span>
                  </div>
                )}
                {result.codice_fiscale && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Codice Fiscale:</span>
                    <span className="font-medium font-mono">{result.codice_fiscale}</span>
                  </div>
                )}
              </div>
              
              {result.notes && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded mb-4">
                  ⚠️ {result.notes}
                </p>
              )}
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetScan} className="flex-1">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Scansiona di nuovo
                </Button>
                <Button onClick={useData} className="flex-1 bg-green-600 hover:bg-green-700">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Usa questi dati
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      {isStreaming && !result && !error && (
        <div className="bg-black/50 px-4 py-6 flex flex-col items-center gap-3">
          <p className="text-white/80 text-xs text-center">
            <Smartphone className="h-4 w-4 inline mr-1" />
            Nessuna foto verrà salvata. Solo i dati verranno copiati.
          </p>
          <Button
            onClick={captureAndScan}
            disabled={isScanning}
            size="lg"
            className="bg-white text-black hover:bg-white/90 rounded-full px-8"
            data-testid="scan-button"
          >
            {isScanning ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <ScanLine className="h-5 w-5 mr-2" />
            )}
            Leggi Documento
          </Button>
        </div>
      )}
      
      {/* Camera permission denied message */}
      {cameraPermission === 'denied' && !error && (
        <div className="absolute inset-0 bg-black flex items-center justify-center p-4">
          <Card className="max-w-sm w-full p-6 text-center">
            <Camera className="h-12 w-12 text-slate-400 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-800 mb-2">Accesso Fotocamera Richiesto</h3>
            <p className="text-sm text-slate-600 mb-4">
              Per scansionare il documento, è necessario consentire l'accesso alla fotocamera.
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => { stopCamera(); onClose?.(); }}>
                Annulla
              </Button>
              <Button onClick={startCamera}>
                Riprova
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DocumentScanner;
